import { action, makeObservable, observable, runInAction } from "mobx";
import OrderAPI from "../api/OrderAPI";
import PaymentLinksAPI from "../api/PaymentLinksAPI";
import { OrdersFilters, OrderType } from "../../../models/Order/Order";
import { OrderBasicDetails } from "../../../models/Order/OrderDetails";
import {
  OrderDeliveryDetails,
  resolveDeliveryRegion,
} from "../../../models/Order/OrderDelivery";
import { OrderPaymentLinkDetails } from "../../../models/Order/OrderPaymentLinkDetails";
import { OrderMockupRequest, OrderRequest } from "../../../models/Order/OrderRequest";
import { FilterValue } from "../../../models/Filters/FilterValue";
import {
  OrderProduct,
  OrderServices,
  PackProduct,
  PackConfigRequest,
} from "../../../models/Order/OrderProduct";
import { Address } from "../../../models/Address/Address";
import { APIBlobResponse } from "../../../models/NetworkResponse";
import { NewUser } from "../../../models/User";
import { OrderMockup } from "../../../models/Order/OrderMockup";
import { MockupComment } from "../../../models/Order/MockupComment";
import { OrderArtwork } from "../../../models/Order/OrderArtwork";
import { OrderActivityLog } from "../../../models/Order/OrderActivityLog";
import { HelperAPI } from "../api/HelperAPI";
import { OrderProposalStats } from "../../../models/Order/OrderProposalStats";
import { OrderProductTatInsight } from "../../../models/Order/OrderProductTatInsight";
import { Invoice } from "../../../models/Invoice/Invoice";
import { Customer } from "../../../models/Customer/Customer";

export class OrderStore {
  orderIds: number[];
  orderIdsAndLabels: { id: number; label: string }[];
  orderAPI: OrderAPI;
  paymentLinksAPI: PaymentLinksAPI;
  orders: OrderBasicDetails[];
  limit: number;
  total_pages: number;
  total_count: number;
  orderRequest: OrderRequest;
  existingOrder: OrderBasicDetails | null;
  productMockup: OrderMockup[];
  mockupComment: MockupComment[];
  packs: PackProduct[];
  orderBasicDetails: OrderBasicDetails | null;
  orderArtworks: OrderArtwork[];
  isLogoLoading: boolean;
  activityLogs: OrderActivityLog[];
  proposalStats: OrderProposalStats | null;
  productTatInsights: OrderProductTatInsight[];
  isProductTatInsightsLoading: boolean;
  isProductTatInsightsOpen: boolean;

  constructor() {
    this.orderIds = [];
    this.orderIdsAndLabels = [];
    this.orderAPI = new OrderAPI();
    this.paymentLinksAPI = new PaymentLinksAPI();
    this.orders = [];
    this.limit = 0;
    this.total_count = 0;
    this.total_pages = 0;
    this.orderRequest = new OrderRequest();
    this.existingOrder = null;
    this.productMockup = [];
    this.mockupComment = [];
    this.packs = [];
    this.orderBasicDetails = null;
    this.orderArtworks = [];
    this.isLogoLoading = false;
    this.activityLogs = [];
    this.proposalStats = null;
    this.productTatInsights = [];
    this.isProductTatInsightsLoading = false;
    this.isProductTatInsightsOpen = false;

    makeObservable(this, {
      orderIds: observable,
      orders: observable,
      orderIdsAndLabels: observable,
      existingOrder: observable,
      orderRequest: observable,
      productMockup: observable,
      mockupComment: observable,
      orderArtworks: observable,
      isLogoLoading: observable,
      activityLogs: observable,
      getOrderActivityLogs: action,
      updatePrimaryImage: action,
      updateSecondaryImage: action,
      searchOrderIds: action,
      removePrimaryImage: action,
      packs: observable,
      removeSecondaryImage: action,
      updateAdditionalInfo: action,
      selectCustomer: action,
      selectUser: action,
      updateBillingAddress: action,
      updateShippingAddress: action,
      addProducts: action,
      addPacks: action,
      updateProductQuantity: action,
      deleteProduct: action,
      deletePack: action,
      addServices: action,
      updateServicesQuantity: action,
      deleteService: action,
      updateOrderDiscount: action,
      updateOrderLabel: action,
      updateOrderStatus: action,
      updateClientPONumber: action,
      createOrUpdateOrder: action,
      clearBillingAddress: action,
      clearShippingAddress: action,
      getOrderDetails: action,
      getOrderListing: action,
      addExchangeRate: action,
      duplicateOrder: action,
      sendTaxInvoice: action,
      searchOrderIdsAndLabels: action,
      downloadPerformaInvoice: action,
      downloadProposalPdf: action,
      removeInvoiceLocally: action,
      reactivateInvoiceLocally: action,
      updateUser: action,
      reset: action,
      orderBasicDetails: observable,
      getOrderBasicDetails: action,
      setOrderBasicDetails: action,
      setProductMockups: action,
      updateSezStatus: action,
      getPacks: action,
      getAllOrderArtworks: action,
      getSingleOrderArtwork: action,
      getCombinedOrderArtworks: action,
      setOrderArtworks: action,
      setLogoLoading: action,
      fetchOrderLogos: action,
      uploadPackImage: action,
      getProposalStats: action,
      proposalStats: observable,
      productTatInsights: observable,
      isProductTatInsightsLoading: observable,
      isProductTatInsightsOpen: observable,
      fetchProductTatInsights: action,
      closeProductTatInsightsPopup: action,
      refreshPaymentLink: action,
      applyOrderDeliveryDetails: action,
    });
  }

  // actions
  applyOrderDeliveryDetails = (details: OrderDeliveryDetails) => {
    if (
      !this.existingOrder ||
      Number(this.existingOrder.id) !== Number(details.order_id)
    ) {
      return;
    }
    this.existingOrder.delivery_region = resolveDeliveryRegion(details);
    this.existingOrder.delivery_type = details.delivery_type;
    this.existingOrder.delivery_location_count =
      details.delivery_location_count;
    this.existingOrder.delivery_cities = details.cities;
    this.existingOrder.delivery_international_remote_count =
      details.delivery_international_remote_count;
    this.existingOrder.delivery_international_bulk_count =
      details.delivery_international_bulk_count;
  };

  updatePrimaryImage = (url: string) => {
    this.orderRequest.primary_logo_url = url;
  };

  updateSecondaryImage = (url: string) => {
    this.orderRequest.secondary_logo_url = url;
  };

  removePrimaryImage = () => {
    this.orderRequest.primary_logo_url = "";
  };

  removeSecondaryImage = () => {
    this.orderRequest.secondary_logo_url = "";
  };

  updateAdditionalInfo = (value: string) => {
    const v = value || "";
    this.orderRequest.comments = v.length > 500 ? v.slice(0, 500) : v;
  };

  selectUser = (selectedValue: FilterValue[]) => {
    if (selectedValue && selectedValue.length !== 0) {
      this.orderRequest = {
        ...this.orderRequest,
        user_id: String(selectedValue[0].id),
        user_name: selectedValue[0].value,
      };
    } else {
      this.orderRequest = {
        ...this.orderRequest,
        user_id: undefined,
        user_name: undefined,
      };
    }
  };

  updateUser = (user?: NewUser) => {
    if (user) {
      this.orderRequest.user = { ...user };
      this.orderRequest = {
        ...this.orderRequest,
        user_id: String(user.id),
        user_name:
          user.first_name + (user.last_name ? " " + user.last_name : ""),
      };
    } else {
      this.orderRequest.user = undefined;
      this.orderRequest = {
        ...this.orderRequest,
        user_id: undefined,
        user_name: undefined,
      };
    }
  };

  selectCustomer = (selectedValue: FilterValue[], customer?: Customer) => {
    if (selectedValue && selectedValue.length !== 0) {
      this.updateUser();

      this.orderRequest = {
        ...this.orderRequest,
        customer_id: String(selectedValue[0].id),
        customer_name: selectedValue[0].value,
        customer,
      };
    } else {
      this.orderRequest = {
        ...this.orderRequest,
        customer_id: undefined,
        customer_name: undefined,
        customer: undefined,
      };
    }
  };

  updateBillingAddress = (address: Address) => {
    this.orderRequest = {
      ...this.orderRequest,
      billing_address_id: address.id,
      billing_address: address,
    };
  };

  updateShippingAddress = (address: Address) => {
    this.orderRequest = {
      ...this.orderRequest,
      shipping_address_id: address.id,
      shipping_address: address,
    };
  };

  updateBillFromAddress = (address: Address) => {
    this.orderRequest = {
      ...this.orderRequest,
      bill_from_address_id: address.id,
      bill_from_address: address,
    };
  };

  clearBillingAddress = () => {
    this.orderRequest = {
      ...this.orderRequest,
      billing_address_id: undefined,
      billing_address: undefined,
    };
  };

  clearShippingAddress = () => {
    this.orderRequest = {
      ...this.orderRequest,
      shipping_address_id: undefined,
      shipping_address: undefined,
    };
  };

  clearBillFromAddress = () => {
    this.orderRequest = {
      ...this.orderRequest,
      bill_from_address_id: undefined,
      bill_from_address: undefined,
    };
  };

  getOrderDetails = async (orderId: number) => {
    try {
      const details = await this.orderAPI.getOrderDetailsById(orderId);
      if (details) {
        this.existingOrder = details;
        this.setOrderDetails();
      }
    } catch (error: any) {
      throw error;
    }
  };

  searchOrderIds = async (searchText: string) => {
    try {
      const list = await this.orderAPI.getOrderIds(searchText);
      this.orderIds = list;
    } catch (error: any) {
      throw error;
    }
  };

  searchOrderIdsAndLabels = async (customerId: number, searchText?: string) => {
    try {
      const list = await this.orderAPI.searchOrderIdsAndLabels(
        customerId,
        searchText,
      );
      this.orderIdsAndLabels = list;
    } catch (error: any) {
      throw error;
    }
  };

  getOrderListing = async (filters: OrdersFilters) => {
    try {
      const response = await this.orderAPI.getOrderListing(filters);
      this.limit = response.limit;
      this.total_count = response.total_count;
      this.total_pages = response.total_pages;
      this.orders = response.list;
    } catch (error: any) {
      throw error;
    }
  };

  isPodOrder = (): boolean =>
    Number(this.existingOrder?.type_id ?? this.orderRequest?.type_id) ===
    OrderType.POD;

  deleteProduct = async (productId: number, orderId?: number) => {
    try {
      const productIndex = this.orderRequest.order_products?.findIndex(
        (product) => product.id === productId,
      );

      if (productIndex !== undefined && productIndex !== -1) {
        if (orderId) {
          const response = await this.orderAPI.deleteProduct(
            productId,
            orderId,
          );
          if (response) {
            this.orderRequest = {
              ...this.orderRequest,
              ...response,
              order_products: response.products as OrderProduct[],
            };
          }
        } else {
          this.orderRequest.order_products =
            this.orderRequest.order_products!.filter(
              (product) => product.id !== productId,
            );
        }
      }
    } catch (error: any) {
      throw error;
    }
  };

  updateProductQuantity = async (
    productId: number,
    value: number,
    field: string,
    orderId?: number,
  ) => {
    try {
      const productIndex = this.orderRequest.order_products?.findIndex(
        (product) => product.id === productId,
      );

      if (productIndex !== undefined && productIndex !== -1) {
        const product = this.orderRequest.order_products![productIndex];

        if (orderId) {
          try {
            if (field === "sellingPrice") {
              const response = await this.orderAPI.updateProductSellingPrice(
                orderId,
                productId,
                value,
              );
              if (response) {
                this.orderRequest = {
                  ...this.orderRequest,
                  ...response,
                  order_products: response.products as OrderProduct[],
                };
              }
            }
          } catch (error) {
            throw error;
          }

          try {
            if (field === "quantity") {
              const response = await this.orderAPI.updateProductQuantity(
                orderId,
                productId,
                value,
              );
              if (response) {
                this.orderRequest = {
                  ...this.orderRequest,
                  ...response,
                  order_products: response.products as OrderProduct[],
                };
              }
            }
          } catch (error) {
            throw error;
          }

          try {
            if (field === "discount%") {
              const response = await this.orderAPI.updateProductDiscount(
                orderId,
                productId,
                value,
              );
              if (response) {
                this.orderRequest = {
                  ...this.orderRequest,
                  ...response,
                  order_products: response.products as OrderProduct[],
                };
              }
            }
          } catch (error) {
            throw error;
          }

          try {
            if (field === "offerPrice") {
              const response = await this.orderAPI.updateProductOfferPrice(
                orderId,
                productId,
                value,
              );
              if (response) {
                this.orderRequest = {
                  ...this.orderRequest,
                  ...response,
                  order_products: response.products as OrderProduct[],
                };
              }
            }
          } catch (error) {
            throw error;
          }

          try {
            if (field === "hsn") {
              const response = await this.orderAPI.updateProductHsn(
                orderId,
                productId,
                value,
              );
              if (response) {
                const updatedProducts = this.orderRequest.order_products?.map(
                  (product) =>
                    product.id === productId
                      ? { ...product, hsn_code: String(value) }
                      : product,
                );

                this.orderRequest = {
                  ...this.orderRequest,
                  order_products: updatedProducts,
                };
              }
            }
          } catch (error) {
            throw error;
          }

          try {
            if (field === "gst") {
              const response = await this.orderAPI.updateProductGst(
                orderId,
                productId,
                value,
              );
              if (response) {
                this.orderRequest = {
                  ...this.orderRequest,
                  ...response,
                  order_products: response.products as OrderProduct[],
                };
              }
            }
          } catch (error) {
            throw error;
          }
        } else {
          if (field === "quantity") {
            product.quantity = value;
          } else if (field === "discount%") {
            product.discount = value;
          } else if (field === "offerPrice") {
            product.unit_price = value;
          } else if (field === "sellingPrice") {
            product.selling_price = value;
          } else if (field === "hsn") {
            product.hsn_code = String(value);
          } else if (field === "gst") {
            product.gst_percentage = value;
          }
        }
      }
    } catch (error) {
      throw error;
    }
  };

  addProducts = async (selectedProducts: OrderProduct[], orderId?: number) => {
    try {
      if (orderId) {
        const response = await this.orderAPI.addProducts(
          selectedProducts,
          orderId,
        );
        if (response) {
          this.orderRequest = {
            ...this.orderRequest,
            ...response,
            order_products: response.products as OrderProduct[],
          };
        }
      } else {
        this.orderRequest.order_products = [
          ...(this.orderRequest.order_products || []),
          ...selectedProducts.map((product) => {
            const existingProduct = this.orderRequest.order_products?.find(
              (existing) => existing.id === product.id,
            );

            if (existingProduct) {
              existingProduct.quantity = product.quantity;
            }
            return product;
          }),
        ];
      }
    } catch (error) {
      throw error;
    }
  };

  addServices = async (selectedServices: OrderServices[], orderId?: number) => {
    try {
      if (orderId) {
        const response = await this.orderAPI.addServices(
          selectedServices,
          orderId,
        );
        if (response && response.products) {
          this.orderRequest = {
            ...this.orderRequest,
            ...response,
            order_services: response.services as OrderServices[],
          };
        }
      } else {
        this.orderRequest.order_services = [
          ...(this.orderRequest.order_services || []),
          ...selectedServices.map((product) => {
            const existingProduct = this.orderRequest.order_services?.find(
              (existing) =>
                existing.id === product.id &&
                existing.custom_name === product.custom_name,
            );

            if (existingProduct) {
              existingProduct.quantity = product.quantity;
            }
            return product;
          }),
        ];
      }
    } catch (error) {
      throw error;
    }
  };

  updateServicesQuantity = async (
    serviceId: number,
    custom_name: string,
    value: number,
    field: string,
    orderId?: number,
  ) => {
    try {
      const productIndex = this.orderRequest.order_services?.findIndex(
        (product) =>
          product.id === serviceId && product.custom_name === custom_name,
      );

      if (productIndex !== undefined && productIndex !== -1) {
        const service = this.orderRequest.order_services![productIndex];

        if (orderId) {
          try {
            if (field === "quantity") {
              const response = await this.orderAPI.updateServiceQuantity(
                orderId,
                serviceId,
                custom_name,
                value,
              );
              if (response) {
                this.orderRequest = {
                  ...this.orderRequest,
                  ...response,
                  order_services: response.services as OrderServices[],
                };
              }
            }
          } catch (error) {
            throw error;
          }
          try {
            if (field === "sellingPrice") {
              const response = await this.orderAPI.updateServiceSellingPrice(
                orderId,
                serviceId,
                custom_name,
                value,
              );
              if (response) {
                this.orderRequest = {
                  ...this.orderRequest,
                  ...response,
                  order_services: response.services as OrderServices[],
                };
              }
            }
          } catch (error) {
            throw error;
          }

          try {
            if (field === "offerPrice") {
              const response = await this.orderAPI.updateServiceOfferPrice(
                orderId,
                serviceId,
                custom_name,
                value,
              );
              if (response) {
                this.orderRequest = {
                  ...this.orderRequest,
                  ...response,
                  order_services: response.services as OrderServices[],
                };
              }
            }
          } catch (error) {
            throw error;
          }
        } else {
          if (field === "quantity") {
            service.quantity = value;
          } else if (field === "sellingPrice") {
            service.selling_price = value;
          } else if (field === "offerPrice") {
            service.unit_price = value;
          }
        }
      }
    } catch (error) {
      throw error;
    }
  };

  deleteService = async (
    serviceId: number,
    custom_name: string,
    orderId?: number,
  ) => {
    try {
      const productIndex = this.orderRequest.order_services?.findIndex(
        (product) =>
          product.id === serviceId && product.custom_name === custom_name,
      );

      if (productIndex !== undefined && productIndex !== -1) {
        if (orderId) {
          const response = await this.orderAPI.deleteService(
            serviceId,
            custom_name,
            orderId,
          );
          if (response) {
            this.orderRequest = {
              ...this.orderRequest,
              ...response,
              order_services: response.services as OrderServices[],
            };
          }
        } else {
          this.orderRequest.order_services =
            this.orderRequest.order_services!.filter(
              (product) =>
                !(
                  product.id === serviceId &&
                  product.custom_name === custom_name
                ),
            );
        }
      }
    } catch (error: any) {
      throw error;
    }
  };

  createOrUpdateOrder = async (orderId?: number) => {
    try {
      this.orderRequest = {
        ...this.orderRequest,
        products: this.orderRequest.order_products,
        services: this.orderRequest.order_services,
        billing_address_id: this.orderRequest.billing_address_id,
        status: this.orderRequest.status,
      };
      if (orderId) {
        const res = await this.orderAPI.updateOrder(this.orderRequest);
        if (res) {
          this.orderRequest = {
            ...this.orderRequest,
            ...res,
            products: this.orderRequest.order_products,
            services: this.orderRequest.order_services,
          } as OrderRequest;
          this.selectCustomer([
            {
              id: String(res.customer?.id),
              value: res.customer?.name || "",
            },
          ], res.customer);
          this.updateUser(res.user);
        }
      } else {
        const response = await this.orderAPI.createOrder(this.orderRequest);
        this.orderRequest = new OrderRequest();
        return response;
      }
    } catch (error: any) {
      throw error;
    }
  };

  updateOrderDiscount = async (orderId: number, discount: number) => {
    try {
      const response = await this.orderAPI.updateOrderDiscount(
        orderId,
        discount,
      );
      if (response) {
        this.orderRequest = {
          ...this.orderRequest,
          ...response,
          order_products: response.products as OrderProduct[],
          order_services: response.services as OrderServices[],
        } as OrderRequest;
      }
    } catch (error: any) {
      throw error;
    }
  };

  addExchangeRate = async (
    orderId: number,
    exchangeRate: number,
    currency: string,
  ) => {
    try {
      const res = await this.orderAPI.addExchangeRate(
        orderId,
        exchangeRate,
        currency,
      );
      if (res) {
        this.orderRequest = {
          ...this.orderRequest,
          ...res,
          order_products: res.products as OrderProduct[],
          order_services: res.services as OrderServices[],
        } as OrderRequest;
      }
    } catch (error: any) {
      throw error;
    }
  };

  updateOrderStatus = (status: number) => {
    this.orderRequest = {
      ...this.orderRequest,
      status: status,
    };
  };

  updateOrderType = (type: number) => {
    this.orderRequest = {
      ...this.orderRequest,
      type_id: type,
    };
  };

  updateOrderLabel = (label: string) => {
    if (label) {
      this.orderRequest = {
        ...this.orderRequest,
        label: label,
      };
    } else {
      this.orderRequest = {
        ...this.orderRequest,
        label: "",
      };
    }
  };

  updateClientPONumber = (poNumber: string) => {
    this.orderRequest = {
      ...this.orderRequest,
      client_po_number: poNumber,
    };
  };

  recalculateOrder = async (orderId: number) => {
    try {
      const res = await this.orderAPI.recalculateOrder(orderId);
      if (res) {
        this.orderRequest = {
          ...this.orderRequest,
          ...res,
          order_products: res.products as OrderProduct[],
          order_services: res.services as OrderServices[],
        } as OrderRequest;
      }
    } catch (error: any) {
      throw error;
    }
  };

  duplicateOrder = async (orderId: number) => {
    try {
      const res = await this.orderAPI.duplicateOrder(orderId);
      return res;
    } catch (error: any) {
      throw error;
    }
  };

  sendTaxInvoice = async (orderId: number) => {
    try {
      const res = await this.orderAPI.sendTaxInvoice(orderId);
      return res;
    } catch (error: any) {
      throw error;
    }
  };

  refreshPaymentLink = async (paymentLinkId: number) => {
    try {
      const response =
        await this.paymentLinksAPI.refreshPaymentLink(paymentLinkId);

      if (response.success && response.result && response.result.payment_link) {
        const updated: OrderPaymentLinkDetails = response.result.payment_link;

        if (this.orderRequest.payment_links) {
          this.orderRequest.payment_links = this.orderRequest.payment_links.map(
            (link: OrderPaymentLinkDetails) =>
              link.id === updated.id ? updated : link,
          );
        }

        if (this.existingOrder?.payment_links) {
          this.existingOrder.payment_links =
            this.existingOrder.payment_links.map(
              (link: OrderPaymentLinkDetails) =>
                link.id === updated.id ? updated : link,
            );
        }
      }

      return response;
    } catch (error: any) {
      throw error;
    }
  };

  downloadSizeMix = async (orderId: number) => {
    try {
      const res = await HelperAPI.downloadAFile("");
      return res;
    } catch (error: any) {
      throw error;
    }
  };

  downloadClientPODocument = async () => {
    try {
      const res = await HelperAPI.downloadAFile(
        this.orderRequest.client_po_url,
      );
      return res;
    } catch (error: any) {
      throw error;
    }
  };

  downloadPerformaInvoice = async (
    orderId: number,
  ): Promise<APIBlobResponse> => {
    try {
      const res = (await this.orderAPI.downloadPerformaInvoice(
        orderId,
      )) as APIBlobResponse;
      return res;
    } catch (error: any) {
      throw error;
    }
  };

  downloadPAndL = async (orderId: number): Promise<any> => {
    try {
      const res = (await this.orderAPI.downloadPAndL(
        orderId,
      )) as APIBlobResponse;
      return res;
    } catch (error: any) {
      throw error;
    }
  };

  downloadProposalPdf = async (
    customerName: string,
    orderId: number,
    proposalCode: string,
  ): Promise<APIBlobResponse> => {
    try {
      const res = await this.orderAPI.downloadProposalPdf(
        customerName,
        orderId,
        proposalCode,
      );
      return res;
    } catch (error: any) {
      throw error;
    }
  };

  reset() {
    this.orderRequest = new OrderRequest();
    this.existingOrder = null;
  }

  removeInvoiceLocally = async (invoiceId: number, reason: string) => {
    const confirmedInvoices = this.orderRequest.invoices;
    if (confirmedInvoices) {
      const updatedInvoices = confirmedInvoices.map((invoice) =>
        invoice.id === invoiceId
          ? {
            ...invoice,
            delete_reason: reason,
            active: false,
          }
          : invoice,
      );

      this.orderRequest = {
        ...this.orderRequest,
        invoices: updatedInvoices,
      };
    }
  };

  reactivateInvoiceLocally = (invoiceId: number, updated?: Invoice) => {
    const confirmedInvoices = this.orderRequest.invoices;
    if (confirmedInvoices) {
      const updatedInvoices = confirmedInvoices.map((invoice) =>
        invoice.id === invoiceId
          ? updated
            ? { ...invoice, ...updated }
            : {
              ...invoice,
              active: true,
              delete_reason: undefined,
            }
          : invoice,
      );

      this.orderRequest = {
        ...this.orderRequest,
        invoices: updatedInvoices,
      };
    }
  };

  fetchPodDeleteSettlement = async (orderId: number) => {
    try {
      return await this.orderAPI.fetchPodDeleteSettlement(orderId);
    } catch (error: any) {
      throw error;
    }
  };

  deleteOrder = async (
    orderId: number,
    payload?: {
      lines?: {
        stock_issue_request_id: number;
        to_swageazy: number;
        to_client: number;
      }[];
      client_inventory_id?: number;
    },
  ) => {
    try {
      const res = await this.orderAPI.deleteorder(orderId, payload);
      return res;
    } catch (error: any) {
      throw error;
    }
  };

  fetchProductMockups = async (orderId: number, productId: number) => {
    try {
      const res = await this.orderAPI.fetchMockups(orderId, productId);
      this.productMockup = res;
    } catch (error: any) {
      throw error;
    }
  };

  uploadMockupsForProduct = async (
    mockups: OrderMockupRequest,
    orderId: number,
  ) => {
    try {
      const res = await this.orderAPI.uploadMockupForProduct(mockups, orderId);
      return res;
    } catch (error: any) {
      throw error;
    }
  };

  generateMockupForProduct = async (payload: {
    order_id: number;
    product_id: number;
    psd_template_id: number;
    logo_url: string;
    x: number;
    y: number;
    width: number;
    height: number;
    rotation?: number;
    mockup_id?: number;
    template_required?: boolean;
    printing_type?: string;
    logo_size?: string;
    rgb_code?: string;
    cmyk_code?: string;
    variant_sku: string;
  }) => {
    try {
      const res = await this.orderAPI.generateOrderMockup(payload);
      return res;
    } catch (error: any) {
      throw error;
    }
  };

  fetchCommentsForProductMockups = async (mockupId: number) => {
    try {
      const res = await this.orderAPI.fetchMockupComments(mockupId);
      this.mockupComment = res;
    } catch (error: any) {
      throw error;
    }
  };

  postCommentForMockup = async (mockupComment: MockupComment) => {
    try {
      const res = await this.orderAPI.postMockupComment(mockupComment);
      return res;
    } catch (error: any) {
      throw error;
    }
  };

  updateCommentLocally = (mockupComment: MockupComment) => {
    this.mockupComment.push({
      mockup_id: mockupComment.mockup_id,
      comment: mockupComment.comments,
      user_id: 0,
    });
  };

  deleteMockup = async (mockupId: number, orderId: number) => {
    try {
      await this.orderAPI.deleteMockup(mockupId, orderId);
      return this.productMockup;
    } catch (error: any) {
      throw error;
    }
  };

  removeMockupImageLocally = (mockupId: number) => {
    if (this.productMockup.length > 0) {
      this.productMockup = this.productMockup.filter(
        (mockup) => mockup.id !== mockupId,
      );
    }
  };

  setOrderDetails = () => {
    if (!this.existingOrder) return;
    const details = this.existingOrder;

    this.orderRequest = {
      ...this.orderRequest,
      type_id: details.type_id,
      customer_id: String(details.customer?.id),
      customer_name: details.customer?.name,
      customer: details.customer,
      user_id: String(details.user?.id),
      user_name: String(
        details.user?.first_name +
        (details.user?.last_name ? " " + details.user?.last_name : ""),
      ),
      primary_logo_url: details.primary_logo_url,
      secondary_logo_url: details.secondary_logo_url,
      billing_address_id: details.billing_address_id,
      comments: details.comments,
      order_products: details.products,
      order_services: details.services,
      products: details.products,
      services: details.services,
      shipping_address_id: details.shipping_address_id,
      label: details.label,
      status: details.status,
      amount_before_discount: Number(details.amount_before_discount),
      net_amount: details.net_amount,
      discount: details.discount,
      tax_amount: details.tax_amount,
      total_amount: details.total_amount,
      order_id: details.id,
      invoices: details.invoices,
      billing_address: details.billing_address,
      shipping_address: details.shipping_address,
      amount_received: details.amount_received,
      discount_percentage: details.discount_percentage,
      client_po_number: details.client_po_number,
      client_po_url: details.client_po_url,
      client_pos: details.client_pos,
      shipping_amount: details.shipping_amount,
      shipping_amount_tax: details.shipping_amount_tax,
      user: details.user,
      exchange_rate: details.exchange_rate,
      currency: details.currency,
      proposal_code: details.proposal_code,
      proposal_url: details.proposal_url,
      tagged_order_ids: details.tagged_order_ids,
      duplicated_from_orderid: details.duplicated_from_orderid,
      is_sez: Boolean(details.is_sez) ? 1 : 0,
      bill_from_address_id: details.bill_from_address_id,
      bill_from_address: details.bill_from_address,
      payment_status: details.payment_status,
      invoice_status: details.invoice_status,
      international_order: Boolean(details.international_order) ? 1 : 0,
      monthly_invoicing: Boolean(details.monthly_invoicing) ? 1 : 0,
    };
  };

  updateAllQuantities = async (orderId: number, quantity: number) => {
    try {
      if (orderId) {
        const response = await this.orderAPI.updateAllQuantities(
          orderId,
          quantity,
        );
        if (response) {
          this.orderRequest = {
            ...this.orderRequest,
            ...response,
            order_products: response.products as OrderProduct[],
          };
        }
      } else {
        if (this.orderRequest.order_products) {
          this.orderRequest.order_products =
            this.orderRequest.order_products.map((product) => ({
              ...product,
              quantity: quantity,
            }));
        }
      }
    } catch (error) {
      throw error;
    }
  };

  getOrderBasicDetails = async (orderId: number) => {
    try {
      const details = await this.orderAPI.getOrderBasicDetails(orderId);
      if (details) {
        this.setOrderBasicDetails(details);
      }
    } catch (error: any) {
      throw error;
    }
  };

  setOrderBasicDetails = (details: OrderBasicDetails) => {
    this.orderBasicDetails = details;
  };

  updateTaggedOrderIds = (taggedOrderIds: any) => {
    this.orderRequest = {
      ...this.orderRequest,
      tagged_order_ids: taggedOrderIds.map((item: any) => item.id).join(","),
    };
  };

  updateStatus = async (status: number, orderId: number) => {
    try {
      const res = await this.orderAPI.updateStatus(status, orderId);
      if (res) {
        this.orderBasicDetails = {
          ...this.orderBasicDetails,
          status: res.status,
        } as OrderBasicDetails;
      }
    } catch (error: any) {
      throw error;
    }
  };

  updateClientPODetails = async (
    orderId: number,
    clientPONumber: string,
    clientPOFileUrl: string,
  ) => {
    try {
      const res = await this.orderAPI.updateClientPODetails(
        orderId,
        clientPONumber,
        clientPOFileUrl,
      );
      if (res) {
        this.orderRequest = {
          ...this.orderRequest,
          client_po_number: clientPONumber,
          client_po_url: clientPOFileUrl,
        };
      }
    } catch (error: any) {
      throw error;
    }
  };

  setProductMockups = (mockups: OrderMockup[]) => {
    runInAction(() => {
      this.mockupComment = [];
      this.productMockup = mockups;
    });
  };

  updateSezStatus = async (orderId: number, is_sez: number) => {
    try {
      const res = await this.orderAPI.updateSezStatus(orderId, is_sez);
      if (res) {
        this.existingOrder = res;
        this.setOrderDetails();
      }
    } catch (error: any) {
      throw error;
    }
  };

  updateInternationalOrderStatus = async (
    orderId: number,
    is_international_order: number,
  ) => {
    try {
      const res = await this.orderAPI.updateInternationalOrderStatus(
        orderId,
        is_international_order,
      );
      if (res) {
        this.existingOrder = res;
        this.setOrderDetails();
      }
    } catch (error: any) {
      throw error;
    }
  };

  updateMonthlyInvoiceStatus = async (
    orderId: number,
    is_monthly_invoice: number,
  ) => {
    try {
      const res = await this.orderAPI.updateMonthlyInvoiceStatus(
        orderId,
        is_monthly_invoice,
      );
      if (res) {
        this.existingOrder = res;
        this.setOrderDetails();
      }
    } catch (error: any) {
      throw error;
    }
  };

  addPacks = async (
    products: PackProduct[],
    label: string,
    quantity: number,
    orderId: number,
  ): Promise<PackProduct> => {
    try {
      const body: PackConfigRequest = {
        products,
        label,
        quantity,
        order_id: orderId,
      };
      const response = await this.orderAPI.addPacks(
        body.products,
        body.label,
        body.quantity,
        body.order_id,
      );
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  getPacks = async (orderId: number) => {
    try {
      const response = await this.orderAPI.getPacks(orderId);
      this.packs = response;
    } catch (error: any) {
      throw error;
    }
  };

  deletePack = async (packId: number, orderId?: number) => {
    try {
      if (orderId) {
        const response = await this.orderAPI.deletePack(packId, orderId);
        if (response) {
          this.orderRequest = {
            ...this.orderRequest,
            ...response,
            order_products: response.products as OrderProduct[],
          };
          // Update the packs observable
          this.packs = this.packs.filter((pack) => pack.id !== packId);
        }
      } else {
        this.orderRequest.order_products =
          this.orderRequest.order_products?.filter(
            (product) => product.id !== packId,
          );
      }
    } catch (error: any) {
      console.error("Error deleting pack:", error);
      throw error;
    }
  };

  getAllOrderArtworks = async (orderId: number) => {
    try {
      const response = await this.orderAPI.getAllOrderArtworks(orderId);
      return response;
    } catch (error) {
      throw error;
    }
  };

  getSingleOrderArtwork = async (orderId: number) => {
    try {
      const response = await this.orderAPI.getSingleOrderArtwork(orderId);
      return response;
    } catch (error) {
      throw error;
    }
  };

  getCombinedOrderArtworks = async (orderId: number) => {
    try {
      const [bulkResponse, singleResponse] = await Promise.allSettled([
        this.getAllOrderArtworks(orderId),
        this.getSingleOrderArtwork(orderId),
      ]);
      let combinedArtworks: OrderArtwork[] = [];

      // Handle single response (product artworks) first
      if (
        singleResponse.status === "fulfilled" &&
        singleResponse.value?.result
      ) {
        const products = singleResponse.value.result;
        if (Array.isArray(products)) {
          products.forEach((product) => {
            // Add artwork images if exist
            if (Array.isArray(product.artWorks)) {
              combinedArtworks.push(...product.artWorks);
            }
          });
        }
      }

      // Add bulk artworks response (direct artwork URLs)
      if (bulkResponse.status === "fulfilled" && bulkResponse.value?.result) {
        combinedArtworks.push(...bulkResponse.value.result);
      }

      return {
        success: true,
        result: combinedArtworks,
      };
    } catch (error) {
      throw error;
    }
  };

  setOrderArtworks = (artworks: OrderArtwork[]) => {
    this.orderArtworks = artworks;
  };

  setLogoLoading = (loading: boolean) => {
    this.isLogoLoading = loading;
  };

  fetchOrderLogos = async (orderId: number) => {
    this.setLogoLoading(true);
    try {
      const response = await this.getCombinedOrderArtworks(orderId);
      if (response?.result && Array.isArray(response.result)) {
        this.setOrderArtworks(response.result);
      } else {
        this.setOrderArtworks([]);
        throw new Error("Failed to fetch logos");
      }
    } catch (error) {
      this.setOrderArtworks([]);
      throw error;
    } finally {
      this.setLogoLoading(false);
    }
  };

  uploadPackImage = async (
    orderId: number,
    packId: number,
    imageUrl: string,
  ) => {
    try {
      const response = await this.orderAPI.uploadPackImage(
        orderId,
        packId,
        imageUrl,
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  approveMockup = async (
    mockupId: number,
    orderId: number,
    approval_note?: string,
    imageUrls?: string[],
  ) => {
    try {
      const response = await this.orderAPI.approveMockup(
        mockupId,
        orderId,
        approval_note,
        imageUrls,
      );

      this.productMockup = this.productMockup.map((mockup) =>
        mockup.id === mockupId
          ? {
            ...mockup,
            approved: 1,
            approved_date: new Date(),
          }
          : mockup,
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  rejectMockup = async (
    mockupId: number,
    orderId: number,
    rejection_note?: string,
    imageUrls?: string[],
  ) => {
    try {
      const response = await this.orderAPI.rejectMockup(
        mockupId,
        orderId,
        rejection_note,
        imageUrls,
      );
      this.productMockup = this.productMockup.map((mockup) =>
        mockup.id === mockupId
          ? {
            ...mockup,
            is_rejected: true,
            rejected_date: new Date(),
          }
          : mockup,
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  getOrderActivityLogs = async (orderId: number) => {
    try {
      const response = await this.orderAPI.getOrderActivityLogs(orderId);
      runInAction(() => {
        this.activityLogs = response;
      });
      return response;
    } catch (error) {
      throw error;
    }
  };

  clearActivityLogs = () => {
    this.activityLogs = [];
  };
  getProposalStats = async (orderId: number) => {
    try {
      const response = await this.orderAPI.getProposalStats(orderId);
      console.log("response", response);
      if (response) {
        this.proposalStats = response.result as OrderProposalStats;
      } else {
        this.proposalStats = null;
      }
    } catch (error) {
      this.proposalStats = null;
      throw error;
    }
  };

  fetchProductTatInsights = async (orderId: number) => {
    this.isProductTatInsightsLoading = true;
    try {
      const response = await this.orderAPI.getProductTatInsights(orderId);
      runInAction(() => {
        this.productTatInsights =
          (response?.result as OrderProductTatInsight[]) || [];
        this.isProductTatInsightsOpen = true;
      });
    } catch (error) {
      runInAction(() => {
        this.productTatInsights = [];
        this.isProductTatInsightsOpen = false;
      });
      throw error;
    } finally {
      runInAction(() => {
        this.isProductTatInsightsLoading = false;
      });
    }
  };

  closeProductTatInsightsPopup = () => {
    this.isProductTatInsightsOpen = false;
    this.productTatInsights = [];
  };
}
